import { useCallback, useEffect, useState } from "react";
import {
  focusWhatsAppBridgeWindow,
  openWhatsAppBridgeWindow,
  probeWhatsAppBridge,
} from "@/modules/despacho/whatsappBridgeClient";

const POLL_MS = 8000;

/**
 * Detecta Visor WhatsApp Bridge en localhost y expone open/focus.
 */
export function useWhatsAppHybridBridge({ enabled = true } = {}) {
  const [bridge, setBridge] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [opening, setOpening] = useState(false);
  const [lastError, setLastError] = useState("");

  const refresh = useCallback(async () => {
    if (!enabled) {
      setBridge(null);
      setLoading(false);
      return;
    }
    const data = await probeWhatsAppBridge();
    setBridge(data);
    setLoading(false);
  }, [enabled]);

  useEffect(() => {
    refresh();
    if (!enabled) return undefined;
    const id = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, refresh]);

  const openHybrid = useCallback(async () => {
    setOpening(true);
    setLastError("");
    const result = await openWhatsAppBridgeWindow();
    if (!result?.ok) {
      setLastError(result?.message || "No se pudo abrir WhatsApp híbrido. ¿Está corriendo el bridge en esta PC?");
      setOpening(false);
      await refresh();
      return result;
    }
    await refresh();
    setOpening(false);
    return result;
  }, [refresh]);

  const focusHybrid = useCallback(async () => {
    setOpening(true);
    setLastError("");
    const result = await focusWhatsAppBridgeWindow();
    if (!result?.ok) {
      setLastError(result?.message || "No se pudo enfocar la ventana híbrida.");
    }
    await refresh();
    setOpening(false);
    return result;
  }, [refresh]);

  return {
    bridgeAvailable: Boolean(bridge?.ok),
    bridgeRunning: Boolean(bridge?.window_running),
    bridgeVersion: bridge?.version || "",
    edgeAvailable: bridge?.edge_available !== false,
    loading,
    opening,
    lastError,
    refresh,
    openHybrid,
    focusHybrid,
  };
}
